import type {Money} from "../../../../lib/Money.ts";
import type {DocumentSettlement, DrillDownLine} from "../../DrillDown/types/types.ts";
import type {Subledger} from "../../SubledgerSelector";
import type { CurrencyEnum } from '../../../../generated/enums.ts';

export enum ViewModeEnum {
    SLIM = "slim",
    CLASSIC = "classic",
}

export enum ReportRowKind {
    Group = "group",
    Account = "account",
    Subtotal = "subtotal",
    Kpi = "kpi",
    BalanceCheck = "balance_check",
}

export enum AmountBucket {
    TrialBalance = "trial_balance",
    NetTrialBalance = "net_trial_balance",
    ProfitAndLoss = "profit_and_loss",
    BalanceSheet = "balance_sheet",
}

export enum ReportLabelKey {
    Assets = "activa",
    Liabilities = "passiva",
    Revenue = "revenue",
    CostOfSales = "cost_of_sales",
    PersonnelExpenses = "personnel_expenses",
    OtherOperatingExpenses = "other_operating_expenses",
    Depreciation = "depreciation",
    FinancialIncomeAndExpenses = "financial_income_and_expenses",
    NonCurrentAssetsIntangible = "non_current_assets_intangible",
    NonCurrentAssetsTangible = "non_current_assets_tangible",
    NonCurrentAssetsFinancial = "non_current_assets_financial",
    CurrentAssetsInventory = "current_assets_inventory",
    CurrentAssetsReceivables = "current_assets_receivables",
    CurrentAssetsSecuritiesAndCash = "current_assets_securities_and_cash",
    Equity = "equity",
    Provisions = "provisions",
    NonCurrentLiabilities = "non_current_liabilities",
    CurrentLiabilities = "current_liabilities",
    GrossMargin = "gross_margin",
    Ebitda = "ebitda",
    Ebit = "ebit",
    Ebt = "ebt",
    NetResult = "net_result",
    BalanceTotal = "balance_total",
    IncomeTaxExpense = "income_tax_expense",
    ProfitAfterTax = "profit_after_tax",
    UnassignedProfitAndLoss = "unassigned_profit_and_loss",
    UnassignedBalanceSheet = "unassigned_balance_sheet",
}

export enum RowWarningReason {
    NotApplicableToLegalEntity = "not_applicable_to_legal_entity",
    NoLayoutSlot = "no_layout_slot",
}

export interface RowWarning {
    reason: RowWarningReason;
    legal_entity_type: string;
}

export interface ReportEntry {
    debit_amount: Money;
    credit_amount: Money;
}

export interface ReportAmounts {
    [AmountBucket.TrialBalance]: ReportEntry;
    [AmountBucket.NetTrialBalance]: ReportEntry;
    [AmountBucket.ProfitAndLoss]: ReportEntry;
    [AmountBucket.BalanceSheet]: ReportEntry;
}

export interface PartialGlAccount {
    code: string;
    label_nl: string;
}

export interface ComparisonCell {
    signed_net: Money;
    delta_ratio: number | null;
}

export interface ReportRow {
    id: string;
    kind: ReportRowKind;
    level: number;
    parent_id: string | null;
    label_key: string | null;
    amounts: ReportAmounts;
    signed_net: Money;
    growth_is_favourable: boolean | null;
    account: PartialGlAccount | null;
    lines: DrillDownLine[];
    document_settlements?: DocumentSettlement[];
    comparison: ComparisonCell | null;
    opening_balance_signed_net: Money | null;
    warning: RowWarning | null;
    subledger?: Subledger | null;
    subledger_split?: boolean;
}

export interface Statement {
    rows: ReportRow[];
}

export interface ComparisonPeriod {
    start_date: string;
    end_date: string;
}

export type BankBalanceCheckStatus = 'matched' | 'mismatch' | 'missing_actual_balance';

export interface BankBalanceCheck {
    bank_account_id: string;
    label: string | null;
    currency: CurrencyEnum;
    derived_balance: Money;
    actual_balance: Money | null;
    difference: Money | null;
    status: BankBalanceCheckStatus;
}

export interface TrialBalanceReport {
    period: {start_date: string; end_date: string};
    currency: CurrencyEnum;
    available_currencies: CurrencyEnum[] | null;
    balance_diff_cents: number;
    bank_balance_checks: BankBalanceCheck[] | null;
    comparison_period: ComparisonPeriod | null;
    profit_and_loss: Statement;
    balance_sheet: Statement;
}

export interface ViewRow extends ReportRow {
    expanded: boolean;
}

export interface KpiPopoverState {
    label: string;
    formula: string;
    value: string;
}

export interface ExportedFile {
    blob: Blob;
    fileName: string;
}
